import React, { useState } from 'react';
import { ArrowLeft, Bell, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopContent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMsg('Please enter a valid email address');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/shop/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.detail || 'Something went wrong');
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  const inner = (
    <div className="flex flex-col items-center justify-center px-4 py-8 gap-8" data-testid="shop-page">
      {/* Only show logo when outside dashboard */}
      {!user && (
        <div className="w-full flex items-center justify-between mb-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            data-testid="shop-back-btn"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <img src="/hibff-logo.png" alt="HiBFF" className="h-16" />
          <div className="w-16" />
        </div>
      )}

      {/* Aspect-ratio wrapper holds the layout in place while the network
          asset is loading so the page doesn't visually collapse on slow
          mobile connections. 3:2 is roughly the natural ratio of the
          coming-soon poster. */}
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
        style={{
          aspectRatio: '3 / 2',
          background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 60%, #F59E0B 100%)',
        }}
      >
        <img
          src="/hibff-shop-coming-soon.webp"
          alt="HiBFF Shop Coming Soon"
          loading="lazy"
          decoding="async"
          width="900"
          height="600"
          className="w-full h-full object-cover"
          data-testid="shop-coming-soon-image"
        />
      </div>
      {/* Notify Me form */}
      <div className="w-full max-w-md" data-testid="notify-section">
        {status === 'success' ? (
          <div className="flex items-center gap-3 p-5 bg-green-50 border border-green-200 rounded-xl text-center justify-center" data-testid="notify-success">
            <CheckCircle size={22} className="text-green-600 flex-shrink-0" />
            <p className="text-sm font-medium text-green-800">
              You&apos;re on the list! We&apos;ll email you when the HiBFF Shop launches.
            </p>
          </div>
        ) : (
          <form onSubmit={handleNotify} className="space-y-3">
            <div className="text-center mb-2">
              <p className="text-lg font-semibold text-gray-800 flex items-center justify-center gap-2">
                <Bell size={18} className="text-orange-500" />
                Get notified when we launch
              </p>
              <p className="text-sm text-gray-500">Be the first to shop exclusive HiBFF merch drops.</p>
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); setErrorMsg(''); }}
                placeholder="your@email.com"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                data-testid="notify-email-input"
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading' || !email.trim()}
                className="px-6 py-3 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="notify-submit-btn"
              >
                {status === 'loading' ? <><Loader2 size={16} className="animate-spin" /> Joining...</> : 'Notify Me'}
              </button>
            </div>
            {status === 'error' && errorMsg && (
              <p className="text-xs text-red-500 text-center" data-testid="notify-error">{errorMsg}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );

  return inner;
};

const Shop = () => {
  const { user } = useAuth();

  if (user) {
    return (
      <DashboardLayout>
        <ShopContent />
      </DashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-orange-50/30 to-rose-50/20">
      <ShopContent />
    </div>
  );
};

export default Shop;
